import React, { useState, useEffect } from 'react';

import { useTheme, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';

import type { Node } from '@xyflow/react';

interface NodeControlFormProps {
  children: React.ReactNode;
  isDisabled?: boolean;
  data: Node['data'];
  updateNodeData: (data: Node['data']) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}

/**
 * A wrapper form component for node controls,
 * Uses CSS grid with automatic expanding last child for overflow handling of big elements such as lists
 */
const NodeControlForm = ({
  children,
  onSubmit,
  data,
  updateNodeData,
  isDisabled = false,
}: NodeControlFormProps): React.ReactElement => {
  const translate = useTranslate();
  const theme = useTheme();
  const [description, setDescription] = useState<string>((data.description as string) ?? '');

  useEffect(() => {
    updateNodeData({ description });
  }, [description, updateNodeData]);

  // only the last children should be 1fr
  const childrenCount = React.Children.count(children);
  const gridTemplateRows = `repeat(${childrenCount}, auto) minmax(250px, 1fr)`;

  return (
    <form
      onSubmit={isDisabled ? undefined : onSubmit}
      onBlur={isDisabled ? undefined : onSubmit}
      style={{ display: 'grid', gridTemplateRows, minHeight: 0, gap: theme.spacing(1) }}
    >
      <TextField
        label={translate('callflows.interface.description')}
        multiline
        rows={3}
        fullWidth
        variant='outlined'
        value={description}
        onChange={(event) => {
          setDescription(event.target.value);
        }}
        sx={{ mb: 1.5 }}
        disabled={isDisabled}
      />
      {children}
    </form>
  );
};

export default NodeControlForm;
